/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Command History Manager
   Bounded undo/redo stack, mergeable commands, zero closures.
   ═══════════════════════════════════════════════════════════════ */

import type { DomainGraphState } from '../types';
import type { DomainCommand } from './types';

export interface CommandHistoryState {
  readonly undoStack: readonly DomainCommand[];
  readonly redoStack: readonly DomainCommand[];
}

export const MAX_HISTORY_STEPS = 100;

export function createEmptyHistory(): CommandHistoryState {
  return {
    undoStack: [],
    redoStack: [],
  };
}

export function executeCommand(
  currentState: DomainGraphState,
  history: CommandHistoryState,
  command: DomainCommand
): { nextState: DomainGraphState; nextHistory: CommandHistoryState } {
  // Check if we can merge with the previous command
  const lastCommand = history.undoStack[history.undoStack.length - 1];
  if (lastCommand && typeof command.mergeWith === 'function') {
    const merged = command.mergeWith(lastCommand);
    if (merged) {
      // Execute the new command on current state
      const nextState = command.execute(currentState);
      const nextHistory: CommandHistoryState = {
        undoStack: [...history.undoStack.slice(0, -1), merged],
        redoStack: [], // clearing redo stack on action
      };
      return { nextState, nextHistory };
    }
  }

  const nextState = command.execute(currentState);
  const nextUndoStack = [...history.undoStack, command];

  if (nextUndoStack.length > MAX_HISTORY_STEPS) {
    nextUndoStack.shift();
  }

  return {
    nextState,
    nextHistory: {
      undoStack: nextUndoStack,
      redoStack: [], // clearing redo on new action
    },
  };
}

export function undoCommand(
  currentState: DomainGraphState,
  history: CommandHistoryState
): { nextState: DomainGraphState; nextHistory: CommandHistoryState } {
  if (history.undoStack.length === 0) {
    return { nextState: currentState, nextHistory: history };
  }

  const command = history.undoStack[history.undoStack.length - 1];
  const nextState = command.undo(currentState);

  return {
    nextState,
    nextHistory: {
      undoStack: history.undoStack.slice(0, -1),
      redoStack: [...history.redoStack, command],
    },
  };
}

export function redoCommand(
  currentState: DomainGraphState,
  history: CommandHistoryState
): { nextState: DomainGraphState; nextHistory: CommandHistoryState } {
  if (history.redoStack.length === 0) {
    return { nextState: currentState, nextHistory: history };
  }

  const command = history.redoStack[history.redoStack.length - 1];
  const nextState = command.execute(currentState);

  return {
    nextState,
    nextHistory: {
      undoStack: [...history.undoStack, command],
      redoStack: history.redoStack.slice(0, -1),
    },
  };
}
